import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPOTypes,
  createPOType,
  updatePOType,
  deletePOType,
  fetchPOTypeChecklist,
  savePOTypeChecklist,
} from "../api/checklists";

// One shared empty array for the loading state. A fresh `[]` default is a new
// identity every render, which re-fires any effect/memo keyed on it — that is
// what looped the dashboard charts (UI audit, Oct 12 2026).
const EMPTY = [];

// The PO type list — the source for POForm's Type dropdown and for
// Settings → Templates → PO Types. A PO type owns the checklist copied onto
// every PO of that type, a subset of the PO's own status vocabulary
// (`status_options` — empty/null means every status stays legal), and a
// "Contains Power Pole" flag.
//
// Mirrors useVisitTypes/useJobTypes deliberately, right down to the
// query-key shape.
export function usePOTypes({ includeInactive = false } = {}) {
  const queryClient = useQueryClient();
  const params = includeInactive ? { include_inactive: true } : {};

  const { data: poTypes = EMPTY, isLoading } = useQuery({
    queryKey: ["poTypes", { includeInactive }],
    queryFn: () => fetchPOTypes(params),
    staleTime: 300_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["poTypes"] });

  const createMutation = useMutation({ mutationFn: createPOType, onSuccess: invalidate });
  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => updatePOType(id, payload),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({ mutationFn: deletePOType, onSuccess: invalidate });

  return {
    poTypes,
    isLoading,
    createPOType: createMutation.mutateAsync,
    updatePOType: updateMutation.mutateAsync,
    deletePOType: deleteMutation.mutateAsync,
    saving:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
}

// One PO type's template. `enabled: false` while nothing is selected.
//
// ⚠️ Saving invalidates BOTH ["poTypes"] (its task_count changes) and
// ["purchaseOrders"] — a PO's own checklist rides along in its serialized
// payload, so a surface holding a stale ["purchaseOrders"] cache would show
// the old task list. Same reasoning as useVisitTypeChecklist.
export function usePOTypeChecklist(poTypeId) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["poTypes", poTypeId, "checklist"],
    queryFn: () => fetchPOTypeChecklist(poTypeId),
    enabled: Boolean(poTypeId),
  });

  const saveMutation = useMutation({
    mutationFn: (items) => savePOTypeChecklist(poTypeId, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["poTypes"] });
      queryClient.invalidateQueries({ queryKey: ["purchaseOrders"] });
    },
  });

  // ⚠️ MEMOIZED, and that is load-bearing, not tidiness — see the identical
  // note on useVisitTypeChecklist's `checklist`. A fresh `[]` on every render
  // while `data` is undefined loops any consumer with an effect keyed on it.
  const checklist = useMemo(() => data?.po || [], [data]);

  return {
    checklist,
    isLoading,
    saveChecklist: saveMutation.mutateAsync,
    saving: saveMutation.isPending,
  };
}
